import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Save, Eye, EyeOff, Lock } from "lucide-react";

const ResetPasswordPage = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [showPassword, setShowPassword] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [isVerifying, setIsVerifying] = useState(false);
	const [isVerified, setIsVerified] = useState(false);
	const [message, setMessage] = useState<string | null>(null);
	const [isError, setIsError] = useState(false);

	// Password validation criteria
	const hasLowercase = /[a-z]/.test(password);
	const hasUppercase = /[A-Z]/.test(password);
	const hasDigit = /[0-9]/.test(password);
	const hasMinLength = password.length >= 6;
	const passwordsMatch = password === confirmPassword;
	const isPasswordValid = hasLowercase && hasUppercase && hasDigit && hasMinLength && passwordsMatch;

	useEffect(() => {
		const verifyOtpOnLoad = async () => {
			try {

				setIsVerifying(true);
				const searchParams = new URLSearchParams(location.search);
				const email = searchParams.get('email');
				const otp = searchParams.get('otp');

                console.log('Verifying OTP with email:', email, 'and OTP:', otp);

				if (!email || !otp) {
					setMessage('Invalid or incomplete reset link. Please check your email for the reset link or request a new one.');
					setIsError(true);
					return;
				}

				// Immediately verify the OTP
				const { error } = await supabase.auth.verifyOtp({
					email: String(email),
					token: String(otp),
					type: 'recovery',
				});

				if (error) {
					console.error('Error verifying OTP:', error);
					//setMessage(error.message || 'Invalid or expired reset link. Please request a new one.');
					setIsError(true);
				} else {
					console.log('OTP verified successfully');
					setIsVerified(true);
				}
			} catch (err) {
				console.error('Unexpected error verifying OTP:', err);
				setMessage('An error occurred while verifying your reset link. Please try again later.');
				setIsError(true);
			} finally {
				setIsVerifying(false);
			}
		};

		verifyOtpOnLoad();
	}, [location.search]);

	const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setPassword(e.target.value);
	};

	const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setConfirmPassword(e.target.value);
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!isPasswordValid) {
			setMessage("Password does not meet requirements or passwords don't match.");
			setIsError(true);
			return;
		}
		
		try {
			setIsLoading(true);
			setMessage(null);
			setIsError(false);

			const { error } = await supabase.auth.updateUser({ password });
			if (error) {
				console.error('Error updating password:', error);
				setMessage(error.message || 'Failed to update password. Please try again.');
				setIsError(true);
				return;
			}
			
			toast.success("Password updated successfully. Redirecting to login...");
			setTimeout(() => navigate("/auth", { replace: true }), 2000);
		} catch (err: any) {
			console.error('Unexpected error in password reset:', err);
			setMessage(err.message || "An unexpected error occurred. Please try again later.");
			setIsError(true);
		} finally {
			setIsLoading(false);
		}
	};

	if (isVerifying) {
		return (
			<div className="flex min-h-screen items-center justify-center">
				<Card className="shadow-lg max-w-md w-full">
					<CardHeader className="text-center pb-6">
						<CardTitle className="text-2xl">Verifying your link</CardTitle>
						<CardDescription>
							Please wait while we verify your reset link...
						</CardDescription>
					</CardHeader>
					<CardContent>
						<div className="flex flex-col items-center justify-center">
							<Lock className="h-10 w-10 text-gray-400 mb-2 animate-pulse" />
						</div>
					</CardContent>
				</Card>
			</div>
		);
	}

	if (!isVerified) {
		return (
			<div className="flex min-h-screen items-center justify-center">
				<Card className="shadow-lg max-w-md w-full">
					<CardHeader className="text-center pb-6">
						<CardTitle className="text-2xl">Reset your password</CardTitle>
						<CardDescription>
							{isError ? "There was a problem with your reset link" : "Follow the link from your email to reset your password."}
						</CardDescription>
					</CardHeader>
					<CardContent>
						<div className="flex flex-col items-center justify-center">
							<Lock className="h-10 w-10 text-gray-400 mb-2" />
							<p className={`text-${isError ? "red-500" : "muted-foreground"}`}>{message}</p>
							{isError && (
								<Button className="mt-4" onClick={() => navigate("/auth", { replace: true })}>
									Return to login
								</Button>
							)}
						</div>
					</CardContent>
				</Card>
			</div>
		);
	}

	return (
		<div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
			<Card className="shadow-lg max-w-md w-full">
				<CardHeader className="text-center pb-6">
					<CardTitle className="text-2xl">Set a new password</CardTitle>
					<CardDescription>Enter your new password below.</CardDescription>
				</CardHeader>
				<form onSubmit={handleSubmit}>
					<CardContent className="space-y-6">
						<div className="space-y-4">
							<div className="space-y-2">
								<Label htmlFor="password">New Password</Label>
								<div className="relative">
									<Input
										id="password"
										type={showPassword ? "text" : "password"}
										value={password}
										onChange={handlePasswordChange}
										placeholder="Enter new password"
										required
									/>
									<Button
										type="button"
										variant="ghost"
										size="sm"
										className="absolute right-2 top-1/2 transform -translate-y-1/2 h-7 w-7 p-0"
										onClick={() => setShowPassword((prev) => !prev)}
									>
										{showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
									</Button>
								</div>
							</div>
							
							<div className="space-y-2">
								<Label htmlFor="confirmPassword">Confirm Password</Label>
								<div className="relative">
									<Input
										id="confirmPassword"
										type={showPassword ? "text" : "password"}
										value={confirmPassword}
										onChange={handleConfirmPasswordChange}
										placeholder="Confirm new password"
										required
									/>
								</div>
							</div>
							
							<div className="text-sm mt-2">
								<p className="mb-1">Password requirements:</p>
								<ul className="space-y-1 list-disc list-inside text-muted-foreground">
									<li className={hasLowercase ? "text-green-500" : ""}>At least one lowercase letter</li>
									<li className={hasUppercase ? "text-green-500" : ""}>At least one uppercase letter</li>
									<li className={hasDigit ? "text-green-500" : ""}>At least one number</li>
									<li className={hasMinLength ? "text-green-500" : ""}>At least 6 characters</li>
									<li className={passwordsMatch ? "text-green-500" : ""}>Passwords match</li>
								</ul>
							</div>
							
							{isError && <div className="text-red-500 text-sm mt-2">{message}</div>}
						</div>
					</CardContent>
					<CardFooter>
						<Button 
							type="submit" 
							className="w-full" 
							disabled={isLoading || !isPasswordValid}
						>
							{isLoading ? "Updating..." : "Update Password"}
							<Save className="ml-2 h-4 w-4" />
						</Button>
					</CardFooter>
				</form>
			</Card>
		</div>
	);
};

export default ResetPasswordPage;
